import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { task } from 'ember-concurrency'
import type { Log } from '@blakeelearning/log'
import type Store from '@ember-data/store'
import { t } from 'ember-intl'
import type { IntlService } from 'ember-intl'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type Student from 'teacher-ui-client/models/student'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type SessionService from 'teacher-ui-client/services/session'
import type SubscriptionUtilisationService from 'teacher-ui-client/services/subscription-utilisation'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import { on } from '@ember/modifier'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import optional from 'teacher-ui-client/helpers/optional'
import { SchoolClassSelect } from 'teacher-ui-client/components/base/select/school-class'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { UiButtonState } from 'teacher-ui-client/components/ui-button/state'

interface Signature {
  Args: {
    /**
     * The selected students to add to a class
     */
    students: Student[]
    /**
     * School classes for the current subscription-type
     */
    schoolClasses: SchoolClass[]
    subscriptionType: SubscriptionType
    onSuccess?: () => unknown
    onCancel?: () => unknown
  }
}

export class FormAddStudentsToClass extends Component<Signature> {
  @service session!: SessionService

  @service store!: Store

  @service intl!: IntlService

  @service log!: Log

  @service subscriptionUtilisation!: SubscriptionUtilisationService

  @service resourceConflict!: ResourceConflictService

  @service flashQueue!: FlashQueueService

  @tracked chosenClassId?: string

  get isSubco() {
    return Boolean(this.session.currentTeacher.subcoFor(this.args.subscriptionType))
  }

  /**
   * Subcos can choose any class, teachers their own classes only
   */
  get possibleClasses() {
    const classes = this.isSubco
      ? this.args.schoolClasses
      : this.args.schoolClasses.filter((sc) => sc.teachers.includes(this.session.currentTeacher))
    return classes.slice().sort((a, b) => a.name.localeCompare(b.name))
  }

  /**
   * students not in the chosen class
   */
  get studentsNotInClass() {
    return this.args.students.filter((student) =>
      student.schoolClasses.every((schoolClass) => schoolClass.id !== this.chosenClassId),
    )
  }

  /**
   * Of the given students, which are unallocated in this subscription? We'll need to check capacity for these before adding to a class.
   */
  get unallocatedStudents() {
    return this.args.students.filter(
      (student) =>
        student.schoolClasses.filter((schoolClass) => schoolClass.subscriptionType === this.args.subscriptionType)
          .length === 0,
    )
  }

  async fetchSubscriptionUtilisation() {
    await this.subscriptionUtilisation.fetchAll()
    this.subscriptionUtilisation.showOverCapacityFlash(this.args.subscriptionType)
  }

  get disableSubmit() {
    return !this.chosenClassId || this.studentsNotInClass.length === 0 || this.addStudentsToClass.isRunning
  }

  updateSchoolClassId = (id: string) => {
    this.chosenClassId = id
  }

  onSubmit = (event: Event) => {
    event.preventDefault()

    if (!this.chosenClassId) return
    const schoolClass = this.store.peekRecord<SchoolClass>('school-class', this.chosenClassId)
    if (!schoolClass) return

    void this.addStudentsToClass.perform(schoolClass, this.studentsNotInClass)
  }

  addStudentsToClass = task({ drop: true }, async (schoolClass: SchoolClass, studentsNotInClass: Student[]) => {
    try {
      // only need to add students not already in the class
      schoolClass.students = [...schoolClass.students, ...studentsNotInClass]
      await schoolClass.save()

      const successMessage = this.intl.t('manage.manageClasses.students.moveStudents.success', {
        schoolClassName: schoolClass.name,
        count: studentsNotInClass.length,
      })
      this.flashQueue.addSuccess({ subtitle: successMessage })

      this.args.onSuccess?.()

      void this.fetchSubscriptionUtilisation()
    } catch (error) {
      if (isConflictError(error)) {
        this.resourceConflict.handleError('failed to add students to class', error)
        return
      }

      this.log.error('failed to add students to class', error ?? undefined)
    }
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    <form {{on "submit" this.onSubmit}} data-test-form-add-students-to-class>
      <div class="flex items-baseline gap-2 p-4">
        <p class="mb-3" data-test-selected-students-text>
          {{t "manage.manageClasses.students.bulkActions.totalSelectedStudents" count=@students.length htmlSafe=true}}
          {{t
            "manage.manageClasses.students.bulkActions.addToClass.selectedStudentsInChosenClass"
            count=this.studentsNotInClass.length
            htmlSafe=true
          }}
        </p>
        <SchoolClassSelect
          data-test-add-students-school-class-select
          class="min-w-0 grow basis-0 truncate"
          @placeholder={{t "manage.manageClasses.students.bulkActions.addToClass.classSelectDefaultOption"}}
          @schoolClasses={{this.possibleClasses}}
          @schoolClassId={{this.chosenClassId}}
          @updateSchoolClassId={{this.updateSchoolClassId}}
          @themeKey={{themeKey @subscriptionType}}
        />
      </div>
      <div class="border-dusty-black-50 border-t p-2 text-right">
        <UiButton data-test-cancel type="reset" {{on "click" (optional @onCancel)}}>
          {{t "manage.manageClasses.students.bulkActions.cancel"}}
        </UiButton>
        <UiButton type="submit" @disabled={{this.disableSubmit}} @appearance={{ButtonAppearance.regular}}>
          <UiButtonState @isLoading={{this.addStudentsToClass.isRunning}}>
            {{t "manage.manageClasses.students.bulkActions.add"}}
          </UiButtonState>
        </UiButton>
      </div>
    </form>
  </template>
}

export default FormAddStudentsToClass
